import { useEffect, useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { useSwiper } from 'swiper/react'

const useStyles = makeStyles({
  arrow: {
    position: 'absolute',
    top: '50%',
    zIndex: 2,
    width: 40,
    height: 40,
    marginTop: -20,
    display: 'grid',
    placeItems: 'center',
    padding: 0,
    border: '1px solid rgba(47, 42, 36, .16)',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, .94)',
    color: 'var(--page-builder-text, #2f2a24)',
    boxShadow: '0 2px 8px rgba(47, 42, 36, .08)',
    cursor: 'pointer',
    '&[hidden]': { display: 'none' },
    '&:disabled': { opacity: .3, cursor: 'default' },
    '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: -4 },
    '& svg': { width: 16, height: 16 },
  },
  previous: { left: 0 },
  next: { right: 0 },
})

/** Instance-local controls also work with Swiper's native CSS scrolling. */
const readNavigationState = (swiper: ReturnType<typeof useSwiper>) => ({
  available: Boolean(swiper.el && !swiper.isLocked && swiper.snapGrid.length > 1),
  beginning: swiper.isBeginning,
  end: swiper.isEnd,
})

const SliderArrows = ({ label, inset = 0 }: { label: string, inset?: number }) => {
  const swiper = useSwiper()
  const classes = useStyles()
  const [state, setState] = useState(() => readNavigationState(swiper))

  useEffect(() => {
    const update = () => {
      const next = readNavigationState(swiper)
      setState(current => current.available === next.available
        && current.beginning === next.beginning
        && current.end === next.end ? current : next)
    }
    const events = ['afterInit', 'progress', 'resize', 'update', 'lock', 'unlock'] as const
    events.forEach(event => swiper.on(event, update))
    update()
    return () => { events.forEach(event => swiper.off(event, update)) }
  }, [swiper])

  return <>
    <button
      type='button'
      className={`${classes.arrow} ${classes.previous}`}
      style={{ left: inset }}
      aria-label={`${label}: nach links scrollen`}
      disabled={state.beginning}
      hidden={!state.available}
      onClick={() => swiper.slidePrev()}
    >
      <svg viewBox='0 0 16 16' fill='none' aria-hidden='true'><path d='M10 3 5 8l5 5' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' /></svg>
    </button>
    <button
      type='button'
      className={`${classes.arrow} ${classes.next}`}
      style={{ right: inset }}
      aria-label={`${label}: nach rechts scrollen`}
      disabled={state.end}
      hidden={!state.available}
      onClick={() => swiper.slideNext()}
    >
      <svg viewBox='0 0 16 16' fill='none' aria-hidden='true'><path d='m6 3 5 5-5 5' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' /></svg>
    </button>
  </>
}

export default SliderArrows
